import { CalendarClock, ChevronDown, LogOut, Moon, Settings, Sun, UserRoundCog } from 'lucide-react';
import * as React from 'react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { getAuthPort } from '@/data/auth';
import { podeAdministracao } from '@/navigation';
import { AdministracaoDialog } from '@/features/administracao/AdministracaoDialog';
import { MeusAgendamentosDialog } from '@/features/meus-agendamentos/MeusAgendamentosDialog';
import { PersonificarInstalacaoDialog } from './personificar-instalacao-dialog';

/**
 * Chip de conta na topbar — mesmo papel do `SessionChip` do self-delivery
 * (avatar + nome + chevron; ao abrir, identidade completa, preferências e
 * ações de conta num lugar só, em vez de espalhadas pela topbar). Faltava
 * isto no rewrite: não havia "Sair" em lugar nenhum do app.
 */

// Duas iniciais (primeiro nome + último sobrenome) — "Paulo Basílio" -> "PB".
// Nome de uma palavra só cai pra uma letra.
function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '?';
  if (partes.length === 1) return partes[0].charAt(0).toUpperCase();
  return (partes[0].charAt(0) + partes[partes.length - 1].charAt(0)).toUpperCase();
}

const ROTULOS_ROLE: Record<string, string> = {
  admin: 'Admin de capabilities',
  'estado-instalacao': 'Estado da instalação',
  'gestao-novidades': 'Gestão de novidades',
  'gerenciar-vinculos': 'Gerenciar vínculos',
  'personificar-instalacao': 'Personificar instalação',
  sysadmin: 'Sysadmin',
  sre: 'SRE',
};

function rotuloRole(role: string): string {
  return ROTULOS_ROLE[role] ?? role;
}

interface UserChipProps {
  usuario: { nome: string; detalhe: string };
  roles: string[];
  escuro: boolean;
  aoAlternarTema: () => void;
}

export function UserChip({ usuario, roles, escuro, aoAlternarTema }: UserChipProps) {
  const [personificarAberto, setPersonificarAberto] = React.useState(false);
  const [administracaoAberta, setAdministracaoAberta] = React.useState(false);
  const [meusAgendamentosAberto, setMeusAgendamentosAberto] = React.useState(false);
  const inicial = iniciais(usuario.nome);
  // `detalhe` normalmente é o e-mail/login — só mostra se não for repetir o nome.
  const mostrarDetalhe = usuario.detalhe && usuario.detalhe !== usuario.nome;
  const podePersonificar =
    roles.includes('personificar-instalacao') || roles.includes('sysadmin') || roles.includes('sre');
  const podeAdmin = podeAdministracao(roles);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-2 rounded-button border border-border bg-bg-surface py-1 pl-1 pr-2.5 shadow-rest transition-colors duration-fast ease-standard hover:border-border-strong hover:bg-bg-subtle"
          >
            <span
              aria-hidden="true"
              className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary font-narrow text-[12px] font-bold text-on-primary"
            >
              {inicial}
            </span>
            <span className="hidden max-w-[10rem] flex-col items-start leading-tight sm:flex">
              <span className="w-full truncate text-[13px] font-bold">{usuario.nome}</span>
              <span className="w-full truncate text-micro font-normal text-on-surface-muted">{usuario.detalhe}</span>
            </span>
            <ChevronDown className="hidden size-4 shrink-0 text-icon sm:block" aria-hidden="true" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent className="min-w-[16rem]">
          <div className="flex items-center gap-2.5 px-2.5 py-2">
            <span
              aria-hidden="true"
              className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary font-narrow text-[14px] font-bold text-on-primary"
            >
              {inicial}
            </span>
            <div className="flex min-w-0 flex-col leading-tight">
              <span className="truncate text-body font-bold text-on-surface">{usuario.nome}</span>
              {mostrarDetalhe ? (
                <span className="truncate text-cell font-normal text-on-surface-muted">{usuario.detalhe}</span>
              ) : null}
            </div>
          </div>

          {roles.length > 0 ? (
            <div className="flex flex-wrap gap-1 px-2.5 pb-2">
              {roles.map((role) => (
                <span
                  key={role}
                  className="rounded-chip bg-bg-subtle px-1.5 py-0.5 text-micro font-normal uppercase tracking-[0.04em] text-on-surface-muted"
                >
                  {rotuloRole(role)}
                </span>
              ))}
            </div>
          ) : null}

          <div className="my-1 h-px bg-border" aria-hidden="true" />

          <DropdownMenuItem onSelect={aoAlternarTema}>
            {escuro ? (
              <Sun className="size-4 shrink-0 text-icon" aria-hidden="true" />
            ) : (
              <Moon className="size-4 shrink-0 text-icon" aria-hidden="true" />
            )}
            {escuro ? 'Usar tema claro' : 'Usar tema escuro'}
          </DropdownMenuItem>

          {podePersonificar ? (
            <DropdownMenuItem onSelect={() => setPersonificarAberto(true)}>
              <UserRoundCog className="size-4 shrink-0 text-icon" aria-hidden="true" />
              Personificar Instalação
            </DropdownMenuItem>
          ) : null}

          <DropdownMenuItem onSelect={() => setMeusAgendamentosAberto(true)}>
            <CalendarClock className="size-4 shrink-0 text-icon" aria-hidden="true" />
            Meus Agendamentos
          </DropdownMenuItem>

          {podeAdmin ? (
            <DropdownMenuItem onSelect={() => setAdministracaoAberta(true)}>
              <Settings className="size-4 shrink-0 text-icon" aria-hidden="true" />
              Administração
            </DropdownMenuItem>
          ) : null}

          <div className="my-1 h-px bg-border" aria-hidden="true" />

          <DropdownMenuItem onSelect={() => getAuthPort().logout()} className="text-danger">
            <LogOut className="size-4 shrink-0" aria-hidden="true" />
            Sair
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <PersonificarInstalacaoDialog aberto={personificarAberto} aoFechar={() => setPersonificarAberto(false)} />
      <MeusAgendamentosDialog aberto={meusAgendamentosAberto} aoFechar={() => setMeusAgendamentosAberto(false)} />
      {podeAdmin ? (
        <AdministracaoDialog aberto={administracaoAberta} aoFechar={() => setAdministracaoAberta(false)} roles={roles} />
      ) : null}
    </>
  );
}
